import * as _ from 'lodash';

import { Deserializer, makeQueryStringSerializer } from 'core/lib/serializer';
import { toFormDataAppeasment } from 'core/util/objects';

import apiFetch from './apiFetch';
import { API_URL } from './constants';
import * as Types from './definitions';
import * as Enums from './enums';

const baseUrl = (clientId: string, programId: string): string => {
    return `${API_URL}/api/v1/client/${clientId}/program/${programId}/Appeasement`;
};

export const defaultRequest: Partial<Types.AppeasementSearchRequest> = {
    page: 1,
    responsibleParties: [Enums.AppeasementResponsibility.Client, Enums.AppeasementResponsibility.GroupO],
    sortDescending: true,
    sortIndex: 'createDate',
    showAppeasementsWithoutPromotion: true,
};

const serializer = makeQueryStringSerializer<Types.AppeasementSearchRequest>(defaultRequest, {
    page: Deserializer.Int,
    sortDescending: Deserializer.Bool,
    showAppeasementsWithoutPromotion: Deserializer.Bool,
});

export default class Appeasement {
    static defaultRequest = defaultRequest;

    static serializeRequest = serializer.serialize;

    static deserializeRequest = serializer.deserialize;

    static async create(clientId: string, programId: string, appeasement: Types.Appeasement): Promise<string> {
        const postAppeasement = _.omit(appeasement, ['id', 'number']);
        const response = await apiFetch(baseUrl(clientId, programId), {
            method: 'POST',
            body: toFormDataAppeasment(postAppeasement),
        });
        const newId = await response.json();

        return newId;
    }

    static async fetchSingle(
        clientId: string,
        programId: string,
        appeasementId: string,
    ): Promise<Types.AppeasementDetail> {
        const response = await apiFetch(`${baseUrl(clientId, programId)}/${appeasementId}`);
        const appeasementDetail = await response.json();
        return appeasementDetail;
    }

    static async review(
        clientId: string,
        programId: string,
        appeasementDetail: Types.AppeasementDetail,
    ): Promise<void> {
        const url = `${baseUrl(clientId, programId)}/${appeasementDetail.id}`;
        const method = appeasementDetail.appeasementState === Enums.AppeasementState.Approved ? 'PUT' : 'DELETE';
        await apiFetch(url, { method });
    }

    static async unapprove(
        clientId: string,
        programId: string,
        appeasementDetail: Types.AppeasementDetail,
    ): Promise<void> {
        const url = `${baseUrl(clientId, programId)}/${appeasementDetail.id}/unapprove`;
        const method = 'PUT';
        await apiFetch(url, { method });
    }

    static async search(
        clientId: string,
        programId: string,
        appeasementSearchRequest: Partial<Types.AppeasementSearchRequest>,
    ): Promise<Types.SearchResultsResponse<Types.AppeasementDetail>> {
        const query = Appeasement.serializeRequest(appeasementSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}?${query}`);
        const appeasements: Types.SearchResultsResponse<Types.AppeasementDetail> = await response.json();
        return appeasements;
    }

    static async searchByBatch(
        clientId: string,
        programId: string,
        batchId: string,
        appeasementSearchRequest: Partial<Types.AppeasementSearchRequest>,
    ): Promise<Types.SearchResultsResponse<Types.AppeasementDetail>> {
        const batchSearchPath = batchId ? `/batch/${batchId}/search` : '';
        const query = Appeasement.serializeRequest(appeasementSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}${batchSearchPath}?${query}`);
        const appeasements: Types.SearchResultsResponse<Types.AppeasementDetail> = await response.json();
        return appeasements;
    }

    static async fetchForCustomer(
        clientId: string,
        programId: string,
        customerId: string,
    ): Promise<Types.AppeasementDetail[]> {
        // eslint-disable-next-line no-restricted-properties
        const maxPageSize = 2 ** 31 - 1;
        const appeasementSearchRequest: Types.AppeasementSearchRequest = {
            ...Appeasement.defaultRequest,
            pageSize: maxPageSize,
        };
        const query = Appeasement.serializeRequest(appeasementSearchRequest, true);
        const url = `${baseUrl(clientId, programId)}/customer/${customerId}/search?${query}`;
        const response = await apiFetch(url);
        const json: Types.SearchResultsResponse<Types.AppeasementDetail> = await response.json();
        return json.results;
    }

    static async fetchForReward(
        clientId: string,
        programId: string,
        rewardId: string,
    ): Promise<Types.AppeasementDetail[]> {
        const response = await apiFetch(`${baseUrl(clientId, programId)}/reward/${rewardId}`);
        const json = await response.json();
        return json;
    }
}
